import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';

interface MetricBoxProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'warning' | 'success' | 'error' | 'info';
  isLoading?: boolean;
}

const MetricBox: React.FC<MetricBoxProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  isLoading = false,
}) => {
  const currentTheme = useAlchemystStoreForAi((store) => store.theme);

  // MUI color mapping
  interface Color {
    light: string;
    main: string;
    text: string;
  }

  interface ColorMap {
    [key: string]: Color;
  }

  const getColor = (colorName: string): Color => {
    const colorMap: ColorMap = {
      primary: {
        light: '#e3f2fd',
        main: '#2196f3',
        text: '#1565c0',
      },
      secondary: {
        light: '#f3e5f5',
        main: '#9c27b0',
        text: '#7b1fa2',
      },
      warning: {
        light: '#fff8e1',
        main: '#ffc107',
        text: '#f57c00',
      },
      success: {
        light: '#e8f5e9',
        main: '#4caf50',
        text: '#2e7d32',
      },
      error: {
        light: '#ffebee',
        main: '#f44336',
        text: '#c62828',
      },
      info: {
        light: '#e8eaf6',
        main: '#3f51b5',
        text: '#283593',
      },
    };

    return colorMap[colorName] || colorMap.primary;
  };

  const colorObj = getColor(color);

  return (
    <Card
      sx={{
        bgcolor: colorObj.light,
        background:
          currentTheme === 'dark'
            ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
            : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
        // background: 'linear-gradient(320deg, rgba(255, 154, 53, 0.125) 1.52%, rgba(0, 0, 0, 0) 90.82%)',
        border: '0.2rem solid transparent',
        borderRadius: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        height: '100%',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {icon && (
              <Typography variant="body1" fontSize="1.2rem">
                {icon}
              </Typography>
            )}
            <Typography variant="subtitle2">{title}</Typography>
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" alignItems="center" height="2.5rem">
            <CircularProgress size={24} color={color} />
          </Box>
        ) : (
          <Typography
            variant="h4"
            component="div"
            //
            // color={colorObj.text}
            mt={1}
          >
            {value}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricBox;
